import React, { useState } from 'react';
import { messagesApi } from '../api';
import { Card, PageTitle, Input, Textarea, Btn, Spinner, Badge } from '../components/common/UI';
import { Send, Plus, Trash2, CheckCircle } from 'lucide-react';
import toast from 'react-hot-toast';

export default function SendMessage() {
  const [mode, setMode] = useState('single');
  const [single, setSingle] = useState({ number: '', message: '' });
  const [bulk, setBulk] = useState([{ number: '', message: '' }]);
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState(null);

  const sendSingle = async () => {
    if (!single.number || !single.message) return toast.error('Fill all fields');
    setLoading(true);
    try {
      const { data } = await messagesApi.send({ number: single.number, message: single.message });
      setResults({ type: 'single', data: data.data });
      toast.success('Message queued!');
      setSingle({ number: '', message: '' });
    } catch (err) {
      const d = err.response && err.response.data;
      toast.error((d && d.error) || 'Send failed');
    } finally {
      setLoading(false);
    }
  };

  const sendBulk = async () => {
    const valid = bulk.filter((m) => m.number && m.message);
    if (valid.length === 0) return toast.error('Add at least one message');
    setLoading(true);
    try {
      const { data } = await messagesApi.bulkSend({ messages: valid });
      setResults({ type: 'bulk', data: data.data });
      toast.success(data.data.queued + ' messages queued!');
      setBulk([{ number: '', message: '' }]);
    } catch (err) {
      const d = err.response && err.response.data;
      toast.error((d && d.error) || 'Bulk send failed');
    } finally {
      setLoading(false);
    }
  };

  const addRow = () => setBulk([...bulk, { number: '', message: '' }]);
  const removeRow = (i) => setBulk(bulk.filter((_, idx) => idx !== i));
  const updateRow = (i, key, val) => setBulk(bulk.map((r, idx) => idx === i ? Object.assign({}, r, { [key]: val }) : r));
  const inputStyle = { width: '100%', padding: '8px 10px', background: '#0f172a', border: '1px solid #334155', borderRadius: 6, color: '#e2e8f0', fontSize: 13, outline: 'none' };

  return (
    <div>
      <PageTitle sub="Send individual or bulk WhatsApp messages">Send Messages</PageTitle>

      <div style={{ display: 'flex', gap: 8, marginBottom: 20 }}>
        {['single', 'bulk'].map((m) => (
          <Btn key={m} variant={mode === m ? 'primary' : 'secondary'} onClick={() => { setMode(m); setResults(null); }}>
            {m === 'single' ? 'Single Message' : 'Bulk Send'}
          </Btn>
        ))}
      </div>

      {mode === 'single' && (
        <Card style={{ maxWidth: 520 }}>
          <h3 style={{ fontSize: 15, fontWeight: 600, color: '#f1f5f9', marginBottom: 16 }}>Send Single Message</h3>
          <Input
            label="Phone Number"
            placeholder="+919876543210"
            value={single.number}
            onChange={(e) => setSingle({ ...single, number: e.target.value })}
          />
          <Textarea
            label="Message"
            placeholder="Type your message..."
            value={single.message}
            onChange={(e) => setSingle({ ...single, message: e.target.value })}
          />
          <Btn onClick={sendSingle} disabled={loading}>
            {loading ? <Spinner size={14} /> : <Send size={14} />}
            Send Message
          </Btn>
        </Card>
      )}

      {mode === 'bulk' && (
        <Card>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
            <h3 style={{ fontSize: 15, fontWeight: 600, color: '#f1f5f9' }}>Bulk Send ({bulk.length} rows)</h3>
            <Btn size="sm" variant="secondary" onClick={addRow}><Plus size={14} /> Add Row</Btn>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {bulk.map((row, i) => (
              <div key={i} style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                <input
                  placeholder="+91..."
                  value={row.number}
                  onChange={(e) => updateRow(i, 'number', e.target.value)}
                  style={Object.assign({}, inputStyle, { width: 180, flexShrink: 0 })}
                />
                <input
                  placeholder="Message..."
                  value={row.message}
                  onChange={(e) => updateRow(i, 'message', e.target.value)}
                  style={Object.assign({}, inputStyle, { flex: 1 })}
                />
                <Btn size="sm" variant="danger" onClick={() => removeRow(i)} disabled={bulk.length === 1}>
                  <Trash2 size={13} />
                </Btn>
              </div>
            ))}
          </div>
          <div style={{ marginTop: 16 }}>
            <Btn onClick={sendBulk} disabled={loading}>
              {loading ? <Spinner size={14} /> : <Send size={14} />}
              Send All ({bulk.filter((r) => r.number && r.message).length} valid)
            </Btn>
          </div>
        </Card>
      )}

      {results && (
        <Card style={{ marginTop: 20 }}>
          <h3 style={{ fontSize: 15, fontWeight: 600, color: '#f1f5f9', marginBottom: 12, display: 'flex', alignItems: 'center', gap: 8 }}>
            <CheckCircle size={16} style={{ color: '#25d366' }} /> Result
          </h3>
          {results.type === 'single' ? (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              <div style={{ display: 'flex', gap: 12 }}>
                <span style={{ color: '#64748b', fontSize: 13 }}>Message ID:</span>
                <code style={{ color: '#25d366', fontSize: 12 }}>{results.data && results.data.messageId}</code>
              </div>
              <div style={{ display: 'flex', gap: 12 }}>
                <span style={{ color: '#64748b', fontSize: 13 }}>Queue Position:</span>
                <span style={{ color: '#e2e8f0', fontSize: 13 }}>{results.data && results.data.queuePosition}</span>
              </div>
            </div>
          ) : (
            <div>
              <div style={{ display: 'flex', gap: 20, marginBottom: 12 }}>
                <span style={{ color: '#25d366', fontSize: 14 }}>✓ Queued: {results.data && results.data.queued}</span>
                <span style={{ color: '#ef4444', fontSize: 14 }}>✗ Failed: {results.data && results.data.failed}</span>
                <span style={{ color: '#64748b', fontSize: 14 }}>Total: {results.data && results.data.total}</span>
              </div>
              <div style={{ maxHeight: 200, overflowY: 'auto' }}>
                {results.data && results.data.results && results.data.results.map((r, i) => (
                  <div key={i} style={{ display: 'flex', justifyContent: 'space-between', padding: '4px 0', borderBottom: '1px solid #334155', fontSize: 13 }}>
                    <span style={{ color: '#94a3b8' }}>{r.number}</span>
                    <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                      {r.success && <span style={{ color: '#64748b', fontSize: 11 }}>{r.messageId}</span>}
                      <Badge color={r.success ? 'green' : 'red'}>{r.success ? 'queued' : 'failed'}</Badge>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </Card>
      )}
    </div>
  );
}
